<!-- Scheduled Order History Dialog -->
<div class="scheduled-order-history-dialog">
  <!-- Summary Stats with Filters -->
  <div class="mb-4 p-3 border-round-lg surface-100">
    <!-- Loading State for Stats -->
    <div *ngIf="isLoading" class="flex align-items-center gap-3 flex-wrap">
      <p-skeleton width="120px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="100px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="130px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="110px" height="2.5rem" borderRadius="9999px" />
      <p-skeleton width="90px" height="2.5rem" borderRadius="9999px" />
    </div>

    <!-- Actual Stats -->
    <div *ngIf="!isLoading" class="flex align-items-center gap-3 flex-wrap">
      <div
        class="stat-badge stat-badge-success"
        [class.stat-badge-active]="typeFilter === 'success'"
        (click)="toggleFilter('success')"
      >
        <i class="pi pi-check-circle"></i>
        <span class="stat-number">{{ getTypeStats().success }}</span>
        <span class="stat-label">Success</span>
      </div>
      <div
        class="stat-badge stat-badge-info"
        [class.stat-badge-active]="typeFilter === 'info'"
        (click)="toggleFilter('info')"
      >
        <i class="pi pi-info-circle"></i>
        <span class="stat-number">{{ getTypeStats().info }}</span>
        <span class="stat-label">Info</span>
      </div>
      <div
        class="stat-badge stat-badge-warning"
        [class.stat-badge-active]="typeFilter === 'warning'"
        (click)="toggleFilter('warning')"
      >
        <i class="pi pi-exclamation-triangle"></i>
        <span class="stat-number">{{ getTypeStats().warning }}</span>
        <span class="stat-label">Warning</span>
      </div>
      <div
        class="stat-badge stat-badge-error"
        [class.stat-badge-active]="typeFilter === 'error'"
        (click)="toggleFilter('error')"
      >
        <i class="pi pi-times-circle"></i>
        <span class="stat-number">{{ getTypeStats().error }}</span>
        <span class="stat-label">Error</span>
      </div>
      <div
        class="stat-badge stat-badge-neutral"
        [class.stat-badge-active]="typeFilter === 'all'"
        (click)="toggleFilter('all')"
      >
        <i class="pi pi-list"></i>
        <span class="stat-number">{{ getTypeStats().total }}</span>
        <span class="stat-label">All</span>
      </div>
    </div>
  </div>

  <!-- Loading State with Skeleton -->
  <div *ngIf="isLoading" class="skeleton-container">
    <!-- Skeleton Header -->
    <div class="skeleton-header">
      <p-skeleton width="80px" height="1.25rem" />
      <p-skeleton width="100px" height="1.25rem" />
      <p-skeleton width="180px" height="1.25rem" />
      <p-skeleton width="120px" height="1.25rem" />
    </div>
    <!-- Skeleton Rows -->
    <div class="skeleton-row" *ngFor="let i of [1, 2, 3, 4]">
      <div class="skeleton-cell" style="width: 80px">
        <p-skeleton width="24px" height="24px" shape="circle" />
      </div>
      <div class="skeleton-cell" style="width: 100px">
        <p-skeleton width="85px" height="1.75rem" borderRadius="16px" />
      </div>
      <div class="skeleton-cell" style="width: 180px">
        <p-skeleton width="160px" height="1.25rem" />
      </div>
      <div class="skeleton-cell" style="flex: 1">
        <p-skeleton [width]="getRandomWidth()" height="1.25rem" />
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="!isLoading && filteredLogs.length === 0" class="empty-state">
    <div class="empty-state-content">
      <i class="pi pi-inbox"></i>
      <h3>No Results</h3>
      <p>No event logs found for this scheduled order.</p>
    </div>
  </div>

  <!-- History Table -->
  <div
    *ngIf="!isLoading && filteredLogs.length > 0"
    class="scheduled-order-history-table"
  >
    <p-table
      [value]="filteredLogs"
      [scrollable]="true"
      scrollHeight="400px"
      styleClass="p-datatable-sm"
      [rowHover]="true"
    >
      <ng-template pTemplate="header">
        <tr>
          <th style="width: 80px">Type</th>
          <th style="width: 100px">Category</th>
          <th style="width: 180px">Timestamp</th>
          <th>Description</th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-log>
        <tr
          [ngClass]="
            'log-type-' + (log.ExceptionType !== 'None' ? 'error' : 'event')
          "
        >
          <!-- Type Icon -->
          <td class="text-center">
            <i
              [class]="getLogIcon(log)"
              [ngClass]="{
                'text-green-500': getLogSeverity(log) === 'success',
                'text-blue-500': getLogSeverity(log) === 'info',
                'text-yellow-500': getLogSeverity(log) === 'warning',
                'text-red-500': getLogSeverity(log) === 'danger',
              }"
              [pTooltip]="log.EventVerb + ' by ' + log.OriginatorFormatted"
            ></i>
          </td>

          <!-- Category -->
          <td>
            <p-tag
              [value]="getLogTypeDisplay(log)"
              [severity]="getLogSeverity(log)"
              styleClass="text-sm"
            />
          </td>

          <!-- Timestamp -->
          <td class="text-sm">
            {{ formatDate(log.EventUtc) }}
          </td>

          <!-- Description -->
          <td class="text-sm" [innerHTML]="getFormattedDescription(log)"></td>
        </tr>
      </ng-template>

      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="4" class="text-center p-4">
            <div class="flex flex-column align-items-center gap-2 text-500">
              <i class="pi pi-inbox text-4xl"></i>
              <p class="text-lg font-medium">No Results</p>
              <p class="text-sm">No event logs match the current filter.</p>
            </div>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>

  <!-- Help Text -->
  <div class="mt-4 p-3 border-round surface-100">
    <div class="text-sm text-600">
      <i class="pi pi-info-circle mr-1"></i>
      <strong>About History:</strong> This shows a chronological record of all
      events and actions performed on this scheduled order, including status
      changes, processing attempts, and user actions.
    </div>
  </div>
</div>
